import { tr } from "./i18n.js";
/**
 * Instagram carousel layout — the pure half of CarouselPanel.jsx.
 *
 * The slides link through ONE continuous lime line. It is a single path
 * across the whole carousel width (slides × 1080 px); each slide renders
 * the same path through an SVG viewBox shifted by its own x offset
 * (`slideViewBox`), so the line meets itself exactly at every swipe. The
 * line sits on the seam between the photo zone (above) and the text zone
 * (below): low on the cover (tall hero photo), higher on inner slides
 * (more room for copy), easing between them across each slide boundary.
 *
 * Slide shape mirrors backend/carousel.py (`blank_slide`). Each slide may
 * override the carousel's style (`style`), pin which part of its photo
 * stays in view (`align`), size its photo by hand (`zoom`/`x`/`y`) and
 * override its photo credit (`credit`).
 *
 * Photo placement. A slide's photo sits in its PHOTO BOX (`photoBox`):
 * the whole slide, or the Frame style's window. With `zoom` 0 the photo
 * is placed automatically (object-fit: cover + the auto / aligned crop).
 * With `zoom` > 0 it was sized by hand: it is `zoom` box widths wide and
 * its top-left corner sits (`x`, `y`) box widths from the box's corner.
 * A panorama uses the same units against the whole strip (slides × 1080).
 *
 * Pure and framework-free — node --test frontend/scripts/carouselLayout.test.mjs
 */

export const SLIDE_W = 1080;
export const SLIDE_H = 1350;
export const MIN_SLIDES = 2;
export const MAX_SLIDES = 10;
export const LIME = "#D9F65C";
export const PAD = 72;
// Hand-sizing bounds, in box widths (backend/carousel.py MIN_ZOOM etc.).
export const MIN_ZOOM = 0.1;
export const MAX_ZOOM = 8;
export const MAX_OFFSET = 20;
export const CHIP_MAX = 28;

export const STYLES = [
  { id: "spotlight", get label() { return tr("Spotlight"); } },
  { id: "split", get label() { return tr("Split"); } },
  { id: "frame", get label() { return tr("Frame"); } },
];
export const DEFAULT_STYLE = "split";

/** Photo alignment grid (backend/carousel.py ALIGNS), row by row. */
export const ALIGNS = [
  { id: "top-left", get label() { return tr("Top left"); }, pos: "0% 0%" },
  { id: "top-center", get label() { return tr("Top center"); }, pos: "50% 0%" },
  { id: "top-right", get label() { return tr("Top right"); }, pos: "100% 0%" },
  { id: "mid-left", get label() { return tr("Mid left"); }, pos: "0% 50%" },
  { id: "center", get label() { return tr("Center"); }, pos: "50% 50%" },
  { id: "mid-right", get label() { return tr("Mid right"); }, pos: "100% 50%" },
  { id: "bottom-left", get label() { return tr("Bottom left"); }, pos: "0% 100%" },
  { id: "bottom-center", get label() { return tr("Bottom center"); }, pos: "50% 100%" },
  { id: "bottom-right", get label() { return tr("Bottom right"); }, pos: "100% 100%" },
];
// Zoom used with a chosen alignment, so every one of the nine positions
// visibly moves the photo (at 1x a landscape photo can only move sideways).
export const ALIGN_SCALE = 1.25;

/** Fill drawn below the line, per style (the text zone). */
export const ZONE_FILL = {
  spotlight: "rgba(0,0,0,0.72)",
  split: "#0B0B0B",
  frame: "rgba(17,24,39,0.92)",
};

export const KINDS = [
  { id: "cover", get label() { return tr("Cover"); } },
  { id: "stat", get label() { return tr("Big number"); } },
  { id: "list", get label() { return tr("List"); } },
  { id: "quote", get label() { return tr("Quote"); } },
  { id: "text", get label() { return tr("Text"); } },
  { id: "closing", get label() { return tr("Closing"); } },
];

const COVER_SEAM = 0.58;   // share of the height above the line
const INNER_SEAM = 0.46;
const WAVE_AMP = 12;
const WAVE_PERIOD = 540;
const STEP = 30;

export function normalizeStyle(style) {
  return STYLES.some((s) => s.id === style) ? style : DEFAULT_STYLE;
}

/** The style one slide renders in: its own, else the carousel's. */
export function slideStyle(slide, carouselStyle) {
  const own = slide && slide.style;
  return STYLES.some((s) => s.id === own) ? own : normalizeStyle(carouselStyle);
}

/** y of the seam at the centre of slide `index`. */
export function seamY(index) {
  return Math.round(SLIDE_H * (index === 0 ? COVER_SEAM : INNER_SEAM));
}

/** Baseline of the line at global x. Flat at the cover's seam across the
 *  cover's left half, then a cosine swoop up to the inner seam that
 *  finishes exactly at the cover's right edge (zero slope there), so
 *  every inner slide starts flat and keeps its full text zone. */
export function baselineAt(x, total) {
  const n = Math.max(1, total);
  const cover = seamY(0);
  if (n === 1 || x <= SLIDE_W / 2) return cover;
  const inner = seamY(1);
  if (x >= SLIDE_W) return inner;
  const t = (x - SLIDE_W / 2) / (SLIDE_W / 2);
  return cover + (inner - cover) * (1 - Math.cos(Math.PI * t)) / 2;
}

export function lineYAt(x, total) {
  return baselineAt(x, total) + WAVE_AMP * Math.sin((2 * Math.PI * x) / WAVE_PERIOD);
}

/** The line as an SVG path across all slides (with a small overhang). */
export function linePath(total) {
  const n = Math.max(1, total);
  const end = n * SLIDE_W + STEP;
  const pts = [];
  for (let x = -STEP; x <= end; x += STEP) {
    pts.push(`${x} ${lineYAt(x, n).toFixed(1)}`);
  }
  return "M" + pts.join(" L");
}

/** The text zone: the line closed down to below the bottom edge. */
export function zonePath(total) {
  const n = Math.max(1, total);
  const end = n * SLIDE_W + STEP;
  return `${linePath(n)} L${end} ${SLIDE_H + 20} L${-STEP} ${SLIDE_H + 20} Z`;
}

export function slideViewBox(index) {
  return `${index * SLIDE_W} 0 ${SLIDE_W} ${SLIDE_H}`;
}

/** Lowest point of the line within a slide — content starts below it. */
export function contentTop(index, total) {
  let max = 0;
  for (let x = index * SLIDE_W; x <= (index + 1) * SLIDE_W; x += STEP / 2) {
    max = Math.max(max, lineYAt(x, total));
  }
  return Math.round(max);
}

/** Highest point of the line within a slide — the Frame style's framed
 *  photo must end above it (the cover's swoop rises on its right half). */
export function photoBottom(index, total) {
  let min = SLIDE_H;
  for (let x = index * SLIDE_W; x <= (index + 1) * SLIDE_W; x += STEP / 2) {
    min = Math.min(min, lineYAt(x, total));
  }
  return Math.round(min);
}

/** Automatic per-slide crop so one photo doesn't look repeated: zoom +
 *  focal point. A slide with an `align` uses that position instead. */
const CROPS = [
  { scale: 1, origin: "50% 50%" },
  { scale: 1.3, origin: "30% 45%" },
  { scale: 1.45, origin: "72% 55%" },
  { scale: 1.2, origin: "50% 85%" },
  { scale: 1.6, origin: "40% 30%" },
];
export function photoCrop(index, align = "") {
  const a = ALIGNS.find((x) => x.id === align);
  if (a) return { scale: ALIGN_SCALE, origin: a.pos, position: a.pos };
  const c = CROPS[((index % CROPS.length) + CROPS.length) % CROPS.length];
  return { ...c, position: "50% 50%" };
}

// ---- photo box + hand placement ------------------------------------------

const FRAME_BORDER = 14;

/** The Frame style's outer window (border included), in slide px. */
export function frameRect(index, total) {
  const top = PAD + 110;
  const left = PAD + 24;
  const bottom = photoBottom(index, total) - 40;
  return { left, top, width: SLIDE_W - 2 * left, height: Math.max(160, bottom - top) };
}

/** Where a slide's photo lives, in slide px: the whole slide, or the
 *  inside of the Frame style's window. */
export function photoBox(style, index, total) {
  if (style !== "frame") return { left: 0, top: 0, width: SLIDE_W, height: SLIDE_H };
  const f = frameRect(index, total);
  return {
    left: f.left + FRAME_BORDER,
    top: f.top + FRAME_BORDER,
    width: f.width - 2 * FRAME_BORDER,
    height: f.height - 2 * FRAME_BORDER,
  };
}

const pct = (s) => (parseFloat(s) || 0) / 100;

/** The rect (relative to the box, px) the automatic placement shows:
 *  object-fit: cover at object-position `crop.position`, then scaled by
 *  `crop.scale` about `crop.origin`. `aspect` = photo width / height. */
export function autoRect(crop, box, aspect) {
  const W = box.width;
  const H = box.height;
  const a = aspect > 0 ? aspect : W / H;
  const w0 = Math.max(W, H * a);
  const h0 = w0 / a;
  const [px, py] = String(crop.position || "50% 50%").split(" ").map(pct);
  const [ox, oy] = String(crop.origin || "50% 50%").split(" ").map(pct);
  const l0 = (W - w0) * px;
  const t0 = (H - h0) * py;
  const s = crop.scale || 1;
  return {
    left: ox * W + (l0 - ox * W) * s,
    top: oy * H + (t0 - oy * H) * s,
    width: w0 * s,
    height: h0 * s,
  };
}

/** The rect (relative to the box, px) of a hand-sized photo. `height`
 *  needs the photo's aspect; without it, null (the <img> sizes itself). */
export function manualRect(p, box, aspect) {
  const W = box.width;
  const width = (p.zoom || 1) * W;
  return {
    left: (p.x || 0) * W,
    top: (p.y || 0) * W,
    width,
    height: aspect > 0 ? width / aspect : null,
  };
}

const r4 = (n) => Math.round(n * 10000) / 10000;
const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

/** Rect (relative to a box of width `boxWidth`) → stored {zoom, x, y}. */
export function rectToPlacement(rect, boxWidth) {
  return {
    zoom: r4(clamp(rect.width / boxWidth, MIN_ZOOM, MAX_ZOOM)),
    x: r4(clamp(rect.left / boxWidth, -MAX_OFFSET, MAX_OFFSET)),
    y: r4(clamp(rect.top / boxWidth, -MAX_OFFSET, MAX_OFFSET)),
  };
}

/** "Fit to width": the photo exactly as wide as its box, top-aligned. */
export const FIT_WIDTH = Object.freeze({ zoom: 1, x: 0, y: 0 });

/** The slide's photo rect relative to its box, whichever mode it's in. */
export function slidePhotoRect(slide, index, box, aspect) {
  if (slide && slide.zoom > 0) return manualRect(slide, box, aspect);
  return autoRect(photoCrop(index, slide && slide.align), box, aspect);
}

/** Width of the strip a panorama spans. */
export function stripWidth(total) {
  return Math.max(1, total) * SLIDE_W;
}

/** The panorama's rect in STRIP px (slide i starts at i × 1080). */
export function panoramaRect(pano, total, aspect) {
  return manualRect(pano || FIT_WIDTH, { width: stripWidth(total) }, aspect);
}

/** Drag one corner of `start` (px rect with width/height) by (dx, dy),
 *  keeping the photo's shape: the opposite corner stays put. The axis
 *  the pointer moved further along wins. Width clamped to [minW, maxW]. */
export function resizeFromCorner(start, corner, dx, dy, { minW = 20, maxW = Infinity } = {}) {
  const aspect = start.width / start.height;
  const sx = corner.includes("e") ? 1 : -1;
  const sy = corner.includes("s") ? 1 : -1;
  const byX = start.width + sx * dx;
  const byY = (start.height + sy * dy) * aspect;
  const w = clamp(Math.abs(byX - start.width) >= Math.abs(byY - start.width) ? byX : byY, minW, maxW);
  const h = w / aspect;
  const ax = sx > 0 ? start.left : start.left + start.width;
  const ay = sy > 0 ? start.top : start.top + start.height;
  return { left: sx > 0 ? ax : ax - w, top: sy > 0 ? ay : ay - h, width: w, height: h };
}

/** The cover's red badge: hidden, the editor's text, or the auto pick. */
export function coverChip({ chip = "", chipHidden = false } = {}, auto = "") {
  if (chipHidden) return "";
  return String(chip || "").trim() || auto || "";
}

/** Font size that keeps `text` inside its box: full size up to
 *  `maxChars`, then shrinks proportionally, never below `min`. */
export function fitFont(text, base, min, maxChars) {
  const len = String(text || "").length;
  if (len <= maxChars) return base;
  return Math.max(min, Math.round((base * maxChars) / len));
}

export function blankSlide(kind = "text") {
  const k = KINDS.some((x) => x.id === kind) ? kind : "text";
  return {
    kind: k, kicker: "", title: "", body: "", stat: "",
    items: [], attribution: "", image: "",
    style: "", credit: "", credit_hidden: false, align: "",
    zoom: 0, x: 0, y: 0,
  };
}

// ---- photos + credits ----------------------------------------------------

const HOST_NOISE = /^(www\d*|cdn\d*|img\d*|images?\d*|static\d*|media\d*|assets?\d*|i\d*|s\d*)\./i;

/** Default credit for a source-article photo — mirrors
 *  backend/carousel.py `source_credit`. */
export function sourceCredit(url) {
  let u;
  try {
    u = new URL(String(url || ""));
  } catch {
    return "";
  }
  let host = u.hostname.toLowerCase();
  if (/^i\d\.wp\.com$/.test(host)) {
    const seg = u.pathname.replace(/^\//, "").split("/")[0].toLowerCase();
    if (seg.includes(".")) host = seg;
  }
  let prev = null;
  while (host !== prev && host.split(".").length > 2) {
    prev = host;
    host = host.replace(HOST_NOISE, "");
  }
  return host ? `Photo: ${host}` : "";
}

/** The featured image's credit — same resolution as StoryCardPanel's
 *  legendText (the article's image legend). */
export function featuredCredit(article) {
  const key = article?.image_legend || "";
  if (!key) return "";
  const text = (article?.image_legend_custom_text || "").trim();
  if (key === "custom") return text;
  if (key === "courtesy") return text ? `Photo courtesy of ${text}` : "";
  if (key === "ai_generated") return "AI-generated illustration";
  if (key === "stock_photo") return "Stock photo, for illustrative purposes";
  return "";
}

const SOURCE_LABEL = { get upload() { return tr("Uploaded"); }, get paste() { return tr("Pasted"); }, pexels: "Pexels", unsplash: "Unsplash" };

/** Distinct photos the editor can put on a slide: the featured image,
 *  then photos added for the carousel, then the source photos. A source
 *  photo not yet copied into our storage has `importFrom` set — the
 *  browser can't draw most outlets' images, so picking it copies it
 *  first; once copied, its option points at the copy. */
export function imageOptions(article) {
  const seen = new Set();
  const out = [];
  const push = (opt) => {
    if (opt.url && !seen.has(opt.url)) {
      seen.add(opt.url);
      out.push(opt);
    }
  };
  push({ url: article?.generated_image_url, label: tr("Featured image"), credit: featuredCredit(article) });
  const lib = (article?.carousel_images || []).filter((e) => e && e.url);
  const copies = {};
  let n = 0;
  lib.forEach((e) => {
    if (e.source === "source" && e.origin_url) {
      copies[e.origin_url] = e;
      return;
    }
    n += 1;
    const who = (e.credit || "").replace(/^Photo( by|:)?\s*/i, "").replace(/\s*(on|\/)\s*(Unsplash|Pexels)$/i, "");
    const base = `${SOURCE_LABEL[e.source] || tr("Added")} photo ${n}`;
    push({ url: e.url, label: who ? `${base} · ${who}` : base, credit: e.credit || "" });
  });
  (article?.source_images || []).forEach((u, i) => {
    if (!u) return;
    const copy = copies[u];
    push(
      copy
        ? { url: copy.url, label: tr("Source photo {value}", { value: i + 1 }), credit: copy.credit || sourceCredit(u) }
        : { url: u, label: tr("Source photo {value}", { value: i + 1 }), credit: sourceCredit(u), importFrom: u },
    );
  });
  return out;
}

/** Map an older slide pick (a raw source URL) to its stored copy. */
function resolveCopy(url, article) {
  const hit = (article?.carousel_images || []).find(
    (e) => e && e.source === "source" && e.origin_url === url && e.url,
  );
  return hit ? hit.url : url;
}

/** The photo a slide shows: its own pick, else the first option. */
export function slideImage(slide, article) {
  const raw = (slide && slide.image) || imageOptions(article)[0]?.url || "";
  return raw ? resolveCopy(raw, article) : "";
}

/** Source photos in use that still need copying into our storage. */
export function pendingSourceImports(slides, article) {
  const sources = new Set(article?.source_images || []);
  const out = new Set();
  (slides || []).forEach((s) => {
    const u = slideImage(s, article);
    if (u && sources.has(u)) out.add(u);
  });
  return [...out];
}

/** Credit a slide auto-uses for its photo (before any override). */
export function autoCredit(slide, article) {
  const url = slideImage(slide, article);
  if (!url) return "";
  const opt = imageOptions(article).find((o) => o.url === url);
  if (opt) return opt.credit || "";
  const lib = (article?.carousel_images || []).find((e) => e && e.url === url);
  if (lib) return lib.credit || "";
  return sourceCredit(url);
}

/** The credit line drawn on a slide: its override, else the auto one.
 *  With a panorama on, the auto one is the panorama's credit. */
export function slideCredit(slide, article, panorama = null) {
  const own = String((slide && slide.credit) || "").trim();
  if (own) return own;
  if (panorama && panorama.image) return panoramaCredit(panorama, article);
  return autoCredit(slide, article);
}

/** What a slide actually draws: nothing when its credit is switched
 *  off (`credit_hidden`), else `slideCredit`. */
export function drawnCredit(slide, article, panorama = null) {
  if (slide && slide.credit_hidden) return "";
  return slideCredit(slide, article, panorama);
}

/** The credit a slide falls back to when it has no text of its own. */
export function defaultCredit(slide, article, panorama = null) {
  return panorama && panorama.image ? panoramaCredit(panorama, article) : autoCredit(slide, article);
}

/** Store an edited credit: text equal to the default is stored as ""
 *  so the slide keeps following its photo (a new photo → new credit). */
export function creditPatch(text, fallback) {
  const t = String(text || "");
  return { credit: t.trim() === String(fallback || "").trim() ? "" : t };
}

/** The panorama's credit: typed, else the photo's own library credit. */
export function panoramaCredit(panorama, article) {
  const own = String((panorama && panorama.credit) || "").trim();
  if (own || !panorama || !panorama.image) return own;
  return autoCredit({ image: panorama.image }, article);
}

// ---- slide list operations (immutable) ---------------------------------

export function moveSlide(slides, index, dir) {
  const j = index + dir;
  if (j < 0 || j >= slides.length) return slides;
  const next = slides.slice();
  [next[index], next[j]] = [next[j], next[index]];
  return next;
}

export function removeSlide(slides, index) {
  if (slides.length <= MIN_SLIDES) return slides;
  return slides.filter((_, i) => i !== index);
}

/** New slide goes before a trailing closing slide, else at the end. */
export function addSlide(slides, kind) {
  if (slides.length >= MAX_SLIDES) return slides;
  const s = blankSlide(kind);
  const last = slides[slides.length - 1];
  if (last && last.kind === "closing") {
    return [...slides.slice(0, -1), s, last];
  }
  return [...slides, s];
}

export function updateSlide(slides, index, patch) {
  return slides.map((s, i) => (i === index ? { ...s, ...patch } : s));
}

/** Items textarea (one per line) ↔ array, max 4 rows. */
export function itemsFromText(text) {
  return String(text || "")
    .split("\n")
    .map((t) => t.trim())
    .filter(Boolean)
    .slice(0, 4);
}

/** Why the carousel can't be posted yet, or "" when it can. */
export function publishProblem(slides, { pageId, caption }) {
  if (!slides || slides.length < MIN_SLIDES) return tr("A carousel needs at least {MIN_SLIDES} slides.", { MIN_SLIDES });
  if (slides.length > MAX_SLIDES) return tr("Instagram allows at most {MAX_SLIDES} slides.", { MAX_SLIDES });
  if (!pageId) return tr("Pick an Instagram account.");
  if (!String(caption || "").trim()) return tr("Write a caption.");
  return "";
}
